import { useVbsTreatmentShowQuery } from "@/services/backend/vbs/treatments/api/treatment-api";
import { Treatment } from "@/services/backend/vbs/treatments/types/treatment";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { SegmentComponent } from "@/services/backend/vbs/treatments/components/segment-component";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { SegmentKind } from "@/services/backend/vbs/treatments/types/segmentKind";
import { TreatmentComponentStateContextProvider } from "@/services/backend/vbs/treatments/components/treatment-component-state-provider";
import {
  ALL_COLUMNS,
  Column,
} from "@/services/backend/vbs/treatments/lib/treatment-component-state";
import { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";

export interface TreatmentComponentProps extends HTMLAttributes<HTMLDivElement> {
  treatmentId: string;
  availableColumns?: Column[];
  defaultColumns?: Column[];
  disabled?: boolean;
}

export function TreatmentComponent({
  treatmentId,
  availableColumns = ALL_COLUMNS,
  defaultColumns = ALL_COLUMNS,
  disabled = false,
  className,
}: TreatmentComponentProps) {
  const {
    data: treatment,
    isLoading,
    error,
  } = useVbsTreatmentShowQuery({
    id: treatmentId,
  });

  if (isLoading) {
    return <TreatmentComponentSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <TreatmentComponentStateContextProvider
      availableColumns={availableColumns!}
      defaultColumns={defaultColumns!}
      disabled={disabled}
    >
      <TreatmentCard treatment={treatment!} className={className} />
    </TreatmentComponentStateContextProvider>
  );
}

interface TreatmentCardProps extends HTMLAttributes<HTMLDivElement> {
  treatment: Treatment;
}

function TreatmentCard({ treatment, className }: TreatmentCardProps) {
  return (
    <div className={cn("rounded-lg border shadow-sm", className)}>
      <Tabs defaultValue={SegmentKind.ExhaustHoods}>
        <TabsList className="grid h-12 w-full grid-cols-5 rounded-b-none border-b px-2">
          {treatment.segments.map((segment) => (
            <TabsTrigger
              value={segment.segmentKind}
              key={segment.segmentKind}
              className="h-9"
            >
              {t(segment.segmentKind)}
            </TabsTrigger>
          ))}
        </TabsList>
        {treatment.segments.map((segment) => (
          <TabsContent value={segment.segmentKind} key={segment.segmentKind}>
            <SegmentComponent
              treatmentId={treatment.id}
              segment={segment}
              positions={treatment.positions}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

type TreatmentComponentSkeletonProps = HTMLAttributes<HTMLDivElement>;

export function TreatmentComponentSkeleton({
  className,
}: TreatmentComponentSkeletonProps) {
  return (
    <div className={cn("rounded-lg border p-6 shadow-sm", className)}>
      <span className="text-muted-foreground mx-auto animate-pulse">
        {t("Lade Leistungen... ")}
      </span>
    </div>
  );
}
